// src/pages/Blog.js
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export default function Blog() {
  const card = {
    hidden: { opacity: 0, y: 30 },
    show: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: 0.15 * i, duration: 0.6, ease: "easeOut" },
    }),
  };

  return (
    <div className="bg-white text-[#0a2545]">
      {/* 🌟 HERO SECTION */}
      <header
        className="relative h-[56vh] md:h-[64vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,37,69,0.55), rgba(13,94,99,0.55)), url('/b.png')",
        }}
      >
        <div className="container mx-auto px-6 md:px-12 text-left">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-extrabold leading-tight text-white drop-shadow-[0_6px_24px_rgba(0,0,0,0.5)]"
          >
            DRIVE YOUR DREAMS
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.8 }}
            className="mt-4 text-lg md:text-xl text-white/90 max-w-2xl"
          >
            Exclusive financing insights, guides, and tips — everything you need
            to get approved and drive away with confidence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-8"
          >
            <Link
              to="/financing"
              className="inline-block bg-teal-400 text-[#0a2545] px-8 py-3 rounded-full font-semibold shadow-lg hover:scale-[1.02] transition"
            >
              Apply for Financing
            </Link>
          </motion.div>
        </div>
      </header>

      {/* 🌟 FEATURED BLOG BANNER */}
      <section className="relative py-20 px-6 md:px-20 bg-gradient-to-r from-[#0a2545] via-[#0d5e63] to-[#0a2545] text-white overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12"
        >
          {/* 🏎️ Featured Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="md:w-1/2 relative"
          >
            <div className="rounded-3xl overflow-hidden shadow-[0_0_25px_rgba(45,212,191,0.4)]">
              <img
               src="/bl4.png"


                alt="Luxury Car Financing"
                className="w-full h-72 object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </motion.div>

          {/* 📰 Featured Text */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="md:w-1/2"
          >
            <h2 className="text-4xl font-extrabold mb-4 tracking-tight">
              How to Finance a Luxury Car in Canada – Step-by-Step Guide
            </h2>
            <p className="text-gray-200 mb-6 leading-relaxed">
              Learn how Canadian drivers can get approved for high-end vehicle
              financing faster — with smart budgeting and flexible plans designed
              for premium cars.
            </p>
            <Link
              to="/blog/luxury-car-financing-guide"
              className="inline-block bg-teal-400 text-[#0a2545] px-8 py-3 rounded-full font-semibold shadow-md hover:bg-white hover:scale-[1.03] transition"
            >
              Read Featured Article →
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* 📰 BLOG LIST */}
      <section className="container mx-auto px-6 md:px-12 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-[#0a2545] mb-6">
          Latest Articles
        </h2>
        <p className="text-gray-600 mb-8 max-w-3xl">
          Carefully curated articles about financing, credit, and choosing the
          right vehicle for your budget — written to help our Canadian clients
          get approved quickly.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Blog 1 */}
          <motion.article
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={1}
            variants={card}
            className="rounded-2xl shadow-xl border border-gray-100 overflow-hidden bg-white hover:shadow-teal-300/50 transition-all"
          >
            <img
              src="/bl1.png"
              alt="Understanding Credit and Approvals"
              className="w-full h-48 object-cover"
            />
            <div className="p-6">
              <h3 className="text-2xl font-semibold text-[#0a2545] mb-3">
                How Credit Really Affects Approvals
              </h3>
              <p className="text-gray-700 mb-4">
                Understand how your credit score and affordability shape your
                approval — and how our financing helps even with low scores.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  By LZ Marketing • 5 min read
                </span>
                <Link
                  to="/blog/how-credit-affects-approvals"
                  className="text-teal-500 font-semibold"
                >
                  Read →
                </Link>
              </div>
            </div>
          </motion.article>

          {/* Blog 2 */}
          <motion.article
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={2}
            variants={card}
            className="rounded-2xl shadow-xl border border-gray-100 overflow-hidden bg-white hover:shadow-teal-300/50 transition-all"
          >
            <img
              src="/bl2.png"
              alt="Top Financing Options 2025"
              className="w-full h-48 object-cover"
            />
            <div className="p-6">
              <h3 className="text-2xl font-semibold text-[#0a2545] mb-3">
                Top Financing Options in Canada (2025)
              </h3>
              <p className="text-gray-700 mb-4">
                Learn about the most competitive financing plans in 2025, and how
                to pick one that fits your budget and credit goals.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  By Yes Auto • 6 min read
                </span>
                <Link
                  to="/blog/top-financing-options-2025"
                  className="text-teal-500 font-semibold"
                >
                  Read →
                </Link>
              </div>
            </div>
          </motion.article>

          {/* Blog 3 */}
          <motion.article
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            custom={3}
            variants={card}
            className="rounded-2xl shadow-xl border border-gray-100 overflow-hidden bg-white hover:shadow-teal-300/50 transition-all"
          >
            <img
              src="/bl3.png"
              alt="Improve Approval Chances"
              className="w-full h-48 object-cover"
            />
            <div className="p-6">
              <h3 className="text-2xl font-semibold text-[#0a2545] mb-3">
                5 Tips to Improve Your Car Loan Approval Chances
              </h3>
              <p className="text-gray-700 mb-4">
                Smart tips to increase your approval odds — from choosing the
                right car to managing your credit effectively.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  By LZ Marketing • 4 min read
                </span>
                <Link
                  to="/blog/5-tips-improve-approval"
                  className="text-teal-500 font-semibold"
                >
                  Read →
                </Link>
              </div>
            </div>
          </motion.article>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            Want personalised help? Our experts will review your application and
            find the best plan.
          </p>
          <Link
  to="/"
  state={{ openApply: true }}
  className="inline-block bg-[#0a2545] text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:scale-[1.02] transition"
>
  Apply for Financing
</Link>

        </div>
      </section>
    </div>
  );
}

